// @flow

import React from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import config from "@config";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  OrganizationHierarchyTree,
  Pagination,
  CollapsableSidebar,
  SectionComponent,
  Row,
  Col,
  GSGrid,
  Icons,
} from "@components";
import { formatDate } from "@transformers/date";
import Stats from "./stats";
import { type PaginationExtendedType } from "@types";

type OwnProps = $Exact<{
  pagination: PaginationExtendedType,
  tree: Object,
  changelog: Object[],
  isSidebarExpanded: boolean,
  toggleSidebar: (isExpanded: boolean) => void,
  onSubmit: () => void,
  onDiscard: () => void,
  onClose: () => void,
  onAdd: (branchId: Object) => void,
  onMove: (branchId: Object) => void,
  onRename: (branchId: Object) => void,
  onDelete: (branchId: Object) => void,
  onView: (branchId: Object) => void,
  onRevert: (changeId: number) => void,
  onDrop: (from: number, to: number) => void,
  onCut: (branchId: ?Object) => void,
  onPageChange: (page: number) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class EditHierarchyModal extends React.Component<Props> {
  componentDidMount() {
    this.props.toggleSidebar(true);
  }

  onAction = (action: string, branch: Object) => {
    switch (action) {
      case "add":
        return this.props.onAdd(branch);
      case "move":
        return this.props.onMove(branch);
      case "cut":
        return this.props.onCut(branch);
      case "rename":
        return this.props.onRename(branch);
      case "delete":
        return this.props.onDelete(branch);
      case "viewScopeChanges":
        return this.props.onView(branch);
      default:
        throw Error(`Unknown action ${action}`);
    }
  };
  onRevert = (changeId: number) => () => this.props.onRevert(changeId);
  render() {
    const totalPages = (this.props.pagination || {}).totalPages;
    return (
      <Modal className="modal-full-screen">
        <ModalHeader toggle={this.props.onClose}>
          {this.props.t("EDIT_DRAFT_HIERARCHY")}
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="d-flex h-100">
            {this.props.tree && (
              <CollapsableSidebar
                onToggle={this.props.toggleSidebar}
                isExpanded={this.props.isSidebarExpanded}
                collapsedWidth={440}
                className="flex-column"
                align="left"
              >
                <SectionComponent>
                  <OrganizationHierarchyTree
                    tree={this.props.tree.structure}
                    expanded={[]}
                    expandAll={true}
                    hoverEmployee={false}
                    onDrop={this.props.onDrop}
                    disabled={false}
                    onAction={this.onAction}
                    searchable={true}
                    searchPlaceholder={this.props.t("FIND_DEPARTMENT")}
                    hideEmployee
                  />
                </SectionComponent>
              </CollapsableSidebar>
            )}
            <SectionComponent
              className="flex-fill edit-draft-modal-table"
              wrapperClassName={classNames("justify-content-center flex-column", {
                "edit-draft-modal__statistic":
                  this.props.changelog && this.props.changelog.length === 0,
              })}
            >
              {this.props.tree && (
                <Stats
                  totalDepartments={this.props.tree.summary.organisationalUnits}
                  created={this.props.tree.summary.addedOrganisationalUnits}
                  deleted={this.props.tree.summary.deletedOrganisationalUnits}
                  moved={this.props.tree.summary.movedOrganisationalUnits}
                  renamed={this.props.tree.summary.renamedOrganisationalUnits}
                />
              )}
              <div className="w-99">
                {this.props.changelog && (
                  <GSGrid
                    rows={this.props.changelog}
                    columns={[
                      {
                        name: this.props.t("CHANGE_ID"),
                        width: 100,
                        renderCell: ({ row }: *) => row.id,
                        key: "id",
                      },
                      {
                        name: this.props.t("OPERATION"),
                        width: 100,
                        renderCell: ({ row }: *) => row.operation,
                        key: "operation",
                      },
                      {
                        name: this.props.t("DESCRIPTION"),
                        renderCell: ({ row }: *) => row.description,
                        key: "description",
                        align: "left",
                      },
                      {
                        name: this.props.t("CHANGED_ON"),
                        width: 120,
                        renderCell: ({ row }: *) => formatDate(row.createdAt),
                        key: "created_at",
                      },
                      {
                        name: this.props.t("CHANGED_BY"),
                        align: "left",
                        width: 120,
                        renderCell: ({ row }: *) =>
                          `${row.createdBy.firstName} ${row.createdBy.lastName}`,
                        key: "created_by",
                      },
                      {
                        name: "",
                        width: 50,
                        renderCell: ({ row }: *) => (
                          <Icons.HistoryJumpIcon
                            className="mr-1 pointer"
                            onClick={this.onRevert(row.id)}
                          />
                        ),
                        key: "revert",
                      },
                    ]}
                  />
                )}
              </div>
              {!!(this.props.pagination && totalPages && totalPages > 1) && (
                <Pagination
                  className="mx-auto d-block"
                  currentPage={this.props.pagination.pageNumber}
                  totalPages={totalPages}
                  onChange={this.props.onPageChange}
                />
              )}
            </SectionComponent>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button
            materialIcon="replay_circle_filled"
            className="float-right ml-3"
            onClick={this.props.onSubmit}
          >
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default localize()(EditHierarchyModal);
